/**
 * Financial cross-validation (FA pack — FA10).
 *
 * Pure cross-check between `FinancialAssumptions.capacityMW` and the
 * upstream Project Intent / SED-implied power requirement. Surfaces
 * a warning strip in the form when financial-implied capacity
 * disagrees with the user-declared intent.
 *
 * PM3 hasn't shipped exact-MW input yet — the canonical
 * `ProjectIntent.powerRequirement` is a 5-bucket union, so we map
 * buckets to representative MW midpoints. When PM3 lands, a new
 * `powerRequirementMW?: number` on ProjectIntent will take
 * precedence via `intentMwHint()`.
 *
 * Pure: no UI, no store. The form reads `crossValidateFinancials()`
 * and renders the warning strip.
 *
 * Unit discipline: every MW field on the result is plain megawatts.
 * No conversion, no scaling.
 */
import type { FinancialAssumptionsCore } from '../types/financialAssumptions';
import type { ProjectIntent } from '../types/siteRag';
import { effectivePhases, bucketMidpointMw as bucketMidpointMwShared, resolveDeclaredLoad } from './projectIntent';

export type CrossValidationSeverity = 'ok' | 'info' | 'warning';

export interface CrossValidationResult {
  severity: CrossValidationSeverity;
  /** Human-readable message — UI strips render this verbatim. */
  message: string;
  /** Financial-side capacity (MW) — null when FA has none. */
  financialMw: number | null;
  /** Intent-side capacity (MW) — null when intent has none / is undecided. */
  intentMw: number | null;
  /** Difference as % of intent MW. null when either side is null. */
  diffPct: number | null;
  /** Did the user explicitly set an exact MW (PM3) vs falling back to a bucket midpoint. */
  intentIsExact: boolean;
}

/**
 * Map a `powerRequirement` bucket to its representative MW basis. Thin
 * wrapper over the shared `bucketMidpointMw()` SSOT (projectIntent.ts) that
 * normalises `undefined` → `null` to preserve this module's historical
 * `number | null` contract. See POWER_BUCKET_MIDPOINT_MW for the values
 * (midpoint of each closed band; max of the open-low band; floor of the
 * open-high band). `undecided` ⇒ null (no signal). Used only as a fallback
 * when PM3's exact-MW field isn't set.
 */
export function bucketMidpointMw(bucket: ProjectIntent['powerRequirement']): number | null {
  return bucketMidpointMwShared(bucket) ?? null;
}

/**
 * Resolve the intent's preferred MW hint:
 *   1. The REAL phased total via `effectivePhases(intent).totalCapacityMW`
 *      when the project is phased — under the CUMULATIVE convention this is
 *      the FINAL-PHASE cumulative (ultimate) capacity the user entered (e.g.
 *      a 50/100/200/300 build → 300 MW, NOT the 650 sum), so a "Project
 *      Intent calls for 300 MW" message matches the ultimate campus exactly.
 *   2. PM3's exact `powerRequirementMW?: number`.
 *   3. Bucket midpoint via `bucketMidpointMw(intent.powerRequirement)`.
 *
 * Returns `{ mw, isExact }`. The phased total and the exact single-MW are
 * both treated as `isExact` (the user gave a concrete number); only the
 * coarse bucket midpoint is inexact.
 */
export function intentMwHint(intent: ProjectIntent | undefined): { mw: number | null; isExact: boolean } {
  if (!intent) return { mw: null, isExact: false };
  // P5 (Stage 5.4) — one resolver. `resolveDeclaredLoad` applies the same
  // three tiers this function used to re-implement (phased ultimate capacity →
  // exact MW → bucket midpoint); only the bucket tier is inexact.
  const declared = resolveDeclaredLoad(intent);
  return { mw: declared.mw, isExact: declared.basis === 'phased' || declared.basis === 'exact' };
}

/**
 * Cross-validate financial-side capacity against intent-side. Returns
 * a structured result the form's warning strip can render.
 *
 * Severity rules:
 *   - ok      ⇒ |diff| ≤ 10% (within typical sizing margin) OR either side null
 *   - info    ⇒ 10% < |diff| ≤ 25% (mild discrepancy; sponsor may be sizing
 *              the financial model conservatively against an over-sized
 *              connection bucket — common during interconnection queue work)
 *   - warning ⇒ |diff| > 25%  OR financial has capacity but intent is set
 *              to a different bucket entirely (e.g. financial says 300 MW
 *              but intent bucket is `under-10mw`).
 *
 * Edge cases:
 *   - Both sides null ⇒ severity 'ok' with "no comparison available".
 *   - Financial null + intent set ⇒ severity 'info' with "enter capacity
 *     to cross-check".
 *   - Financial set + intent undecided ⇒ severity 'ok'.
 */
export function crossValidateFinancials(
  fa: FinancialAssumptionsCore | null | undefined,
  intent: ProjectIntent | undefined,
): CrossValidationResult {
  const financialMw = fa?.capacityMW ?? null;
  const hint = intentMwHint(intent);
  const intentMw = hint.mw;

  if (financialMw == null && intentMw == null) {
    return {
      severity: 'ok',
      message: 'No comparison available — capacity not set on either side.',
      financialMw, intentMw, diffPct: null, intentIsExact: hint.isExact,
    };
  }

  if (financialMw == null && intentMw != null) {
    const exactNote = hint.isExact ? '' : ' (bucket midpoint)';
    return {
      severity: 'info',
      message: `Project Intent calls for ${intentMw} MW${exactNote}, but no financial-model capacity is set yet.`,
      financialMw, intentMw, diffPct: null, intentIsExact: hint.isExact,
    };
  }

  if (financialMw != null && intentMw == null) {
    return {
      severity: 'ok',
      message: `Financial model: ${financialMw} MW. Project Intent power requirement is undecided.`,
      financialMw, intentMw, diffPct: null, intentIsExact: hint.isExact,
    };
  }

  // Both set — compute the diff.
  const fmw = financialMw as number;
  const imw = intentMw as number;
  const diff = Math.abs(fmw - imw);
  const diffPct = (diff / Math.max(imw, 0.001)) * 100;

  let severity: CrossValidationSeverity = 'ok';
  let message = '';
  const intentSourceLabel = hint.isExact ? 'exact MW' : 'bucket midpoint';

  if (diffPct <= 10) {
    severity = 'ok';
    message = `Capacity check passes: financial ${fmw} MW vs intent ${imw} MW (${intentSourceLabel}). Δ ${diffPct.toFixed(1)}%.`;
  } else if (diffPct <= 25) {
    severity = 'info';
    message = `Mild discrepancy: financial ${fmw} MW vs intent ${imw} MW (${intentSourceLabel}). Δ ${diffPct.toFixed(1)}% — confirm sizing.`;
  } else {
    severity = 'warning';
    message = `Large discrepancy: financial ${fmw} MW vs intent ${imw} MW (${intentSourceLabel}). Δ ${diffPct.toFixed(1)}% — sync either side.`;
  }

  return {
    severity, message, financialMw, intentMw, diffPct, intentIsExact: hint.isExact,
  };
}

/**
 * Bring the financial-side capacity into alignment with the intent
 * MW. Returns a patch: `{ capacityMW: <intent mw> }`. The form's
 * one-click "Sync to intent" button passes this through
 * `updateSiteFinancialAssumptionsField(siteId, 'capacityMW', mw, …)`.
 *
 * Returns null when the intent has no MW to sync against
 * (undecided / no intent set).
 */
export function syncCapacityToIntentPatch(
  intent: ProjectIntent | undefined,
): { capacityMW: number } | null {
  const hint = intentMwHint(intent);
  if (hint.mw == null) return null;
  return { capacityMW: hint.mw };
}
